import { ErrorModal } from "@dexteel/mesf-core";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type {
	CellClassParams,
	ColDef,
	ColGroupDef,
	ITooltipParams,
} from "ag-grid-enterprise";
import { useMemo, useState } from "react";
import { globalDefaultColDef, mesfColors } from "@/setup/ag-grid/mesf-theme";
import {
	useCurrentCampaign,
	useFutureCampaigns,
} from "../hooks/useNextCampaigns";
import {
	CHANGE_SUGGESTION_COLORS,
	CHANGE_SUGGESTION_TOOLTIPS,
	type ChangeSuggestion,
	type CurrentCampaignStand,
	type FutureCampaignHeader,
	type FutureCampaignStandDetail,
	type CurrentCampaignHeader,
} from "../models/NextCampaigns";
import { LegendBar } from "./LegendBar";

const groupSeparator = "ag-column-group-separator";

const renderLabeledLine = (line: string) => {
	// A label runs to its colon and may be several words ("Current Billets:").
	// Its words are joined by a single space, which is what stops it from
	// swallowing the value before it: pairs on one line are separated by two.
	const parts = line.split(/((?:[A-Za-z]+ )*[A-Za-z]+:)/g);
	if (parts.length <= 1) return line;
	return parts.map((part, idx) => {
		const isLabel = /^(?:[A-Za-z]+ )*[A-Za-z]+:$/.test(part);
		return (
			<span key={idx} style={{ fontWeight: isLabel ? 700 : 400 }}>
				{part}
			</span>
		);
	});
};

const CampaignGroupHeader = (props: { displayName: string }) => {
	const lines = props.displayName.split("\n");
	return (
		<div
			style={{
				lineHeight: 1.2,
				padding: "2px 0",
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				textAlign: "center",
				gap: "7px",
				width: "100%",
			}}
		>
			{lines.map((line, i) => (
				<div
					key={line}
					style={
						i === 0
							? { fontWeight: 700, fontSize: "15px" }
							: {
									fontSize: "13px",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}
					}
					title={line}
				>
					{i === 0 ? line : renderLabeledLine(line)}
				</div>
			))}
		</div>
	);
};

const formatShortDate = (dateStr: string | null): string => {
	if (!dateStr) return "";
	const d = new Date(dateStr);
	if (Number.isNaN(d.getTime())) return dateStr;
	return `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getDate().toString().padStart(2, "0")}/${d.getFullYear()}`;
};

const buildCurrentHeaderName = (
	header: CurrentCampaignHeader | null,
): string => {
	if (!header) return "No active campaign";
	return [
		header.ProductSizeName,
		`Billets: ${header.ScheduledBillets}  Current Billets: ${header.CurrentBillets}`,
		`Wo: ${header.WorkOrderName}`,
	].join("\n");
};

const buildFutureHeaderName = (header: FutureCampaignHeader): string => {
	const date = formatShortDate(header.ScheduledDate);
	return [
		header.ProductSizeName,
		`Billets: ${header.ScheduledBillets}${date ? `  Date: ${date}` : ""}`,
		`Wo: ${header.WorkOrderName}`,
	].join("\n");
};

interface GridRow {
	standName: string;
	standOrder: number;
	cur_pass: string | null;
	cur_count: number | null;
	cur_max: number | null;
	cur_cart: string | null;
	[key: string]: unknown;
}

const buildRowData = (
	stands: CurrentCampaignStand[],
	standDetails: FutureCampaignStandDetail[],
): GridRow[] => {
	const sorted = [...stands].sort((a, b) => a.StandOrder - b.StandOrder);

	return sorted.map((stand) => {
		const row: GridRow = {
			standName: stand.StandName,
			standOrder: stand.StandOrder,
			cur_pass: stand.PassName,
			cur_count: stand.PassCount,
			cur_max: stand.PassCountMax,
			cur_cart: stand.CartridgeName,
		};

		for (const detail of standDetails) {
			if (detail.StandId === stand.StandId) {
				row[`f${detail.rn}_pass`] = detail.PassName;
				row[`f${detail.rn}_cart`] = detail.Cartridge;
				row[`f${detail.rn}_cs`] = detail.ChangeSuggestion;
			}
		}

		return row;
	});
};

const makeFutureCellStyle = (rn: number) => {
	return (params: CellClassParams<GridRow>) => {
		const cs = params.data?.[`f${rn}_cs`] as ChangeSuggestion | undefined;
		const pass = params.data?.[`f${rn}_pass`] as string | null | undefined;
		if (!pass || !cs) return null;
		return { backgroundColor: CHANGE_SUGGESTION_COLORS[cs] };
	};
};

const makeFutureTooltip = (rn: number) => {
	return (params: ITooltipParams<GridRow>) => {
		const cs = params.data?.[`f${rn}_cs`] as ChangeSuggestion | undefined;
		const pass = params.data?.[`f${rn}_pass`] as string | null | undefined;
		if (!pass || !cs) return undefined;
		return CHANGE_SUGGESTION_TOOLTIPS[cs];
	};
};

const buildColumnDefs = (
	currentHeader: CurrentCampaignHeader | null,
	futureHeaders: FutureCampaignHeader[],
	showCurrentCampaign: boolean,
): (ColDef<GridRow> | ColGroupDef<GridRow>)[] => {
	const standCol: ColDef<GridRow> = {
		field: "standName",
		headerName: "",
		pinned: "left",
		width: 55,
		sortable: false,
		suppressMovable: true,
		cellStyle: { fontWeight: 600, fontSize: "11px" },
	};

	const currentGroup: ColGroupDef<GridRow> = {
		headerName: buildCurrentHeaderName(currentHeader),
		headerGroupComponent: CampaignGroupHeader,
		headerClass: groupSeparator,
		marryChildren: true,
		children: [
			{
				field: "cur_pass",
				headerName: "Pass",
				minWidth: 100,
				flex: 1,
				sortable: false,
				headerClass: groupSeparator,
			},
			{
				field: "cur_count",
				headerName: "Count",
				width: 65,
				sortable: false,
				type: "rightAligned",
				cellStyle: { color: mesfColors.success.dark, fontWeight: 600 },
			},
			{
				field: "cur_max",
				headerName: "Max",
				width: 65,
				sortable: false,
				type: "rightAligned",
				cellStyle: { color: mesfColors.success.dark, fontWeight: 600 },
			},
			{
				field: "cur_cart",
				headerName: "Cart",
				width: 65,
				sortable: false,
				type: "rightAligned",
				headerClass: groupSeparator,
				cellClass: groupSeparator,
				cellStyle: { color: mesfColors.success.dark, fontWeight: 600 },
			},
		],
	};

	const futureGroups: ColGroupDef<GridRow>[] = futureHeaders.map((h) => ({
		headerName: buildFutureHeaderName(h),
		headerGroupComponent: CampaignGroupHeader,
		headerClass: groupSeparator,
		marryChildren: true,
		children: [
			{
				field: `f${h.rn}_pass`,
				headerName: "Pass",
				minWidth: 120,
				maxWidth: 250,
				flex: 1,
				sortable: false,
				cellStyle: makeFutureCellStyle(h.rn),
				tooltipValueGetter: makeFutureTooltip(h.rn),
			},
			{
				field: `f${h.rn}_cart`,
				headerName: "Cartridge",
				width: 80,
				maxWidth: 120,
				sortable: false,
				cellStyle: makeFutureCellStyle(h.rn),
				tooltipValueGetter: makeFutureTooltip(h.rn),
				headerClass: groupSeparator,
				cellClass: groupSeparator,
			},
		],
	}));

	const cols: (ColDef<GridRow> | ColGroupDef<GridRow>)[] = [standCol];
	if (showCurrentCampaign) {
		cols.push(currentGroup);
	}
	cols.push(...futureGroups);
	return cols;
};

const CAMPAIGNS_PER_PAGE = 6;

export const NextCampaigns = () => {
	const {
		data: currentData,
		isFetching: isFetchingCurrent,
		error: currentError,
	} = useCurrentCampaign();
	const {
		data: futureData,
		isFetching: isFetchingFuture,
		error: futureError,
	} = useFutureCampaigns();
	const [error, setError] = useState("");
	const [page, setPage] = useState(0);

	const isLoading =
		!currentData && !futureData && (isFetchingCurrent || isFetchingFuture);

	const allHeaders = futureData?.headers ?? [];
	// Page 0: current campaign + first (N-1) future campaigns
	// Page 1+: N future campaigns each
	const futureOnFirstPage = CAMPAIGNS_PER_PAGE - 1;
	const remainingAfterFirst = Math.max(
		0,
		allHeaders.length - futureOnFirstPage,
	);
	const totalPages = Math.max(
		1,
		1 + Math.ceil(remainingAfterFirst / CAMPAIGNS_PER_PAGE),
	);

	const showCurrentCampaign = page === 0;

	const pageHeaders = useMemo(() => {
		if (page === 0) {
			return allHeaders.slice(0, futureOnFirstPage);
		}
		const offset = futureOnFirstPage + (page - 1) * CAMPAIGNS_PER_PAGE;
		return allHeaders.slice(offset, offset + CAMPAIGNS_PER_PAGE);
	}, [allHeaders, page, futureOnFirstPage]);

	const rowData = useMemo(
		() =>
			buildRowData(currentData?.stands ?? [], futureData?.standDetails ?? []),
		[currentData?.stands, futureData?.standDetails],
	);

	const columnDefs = useMemo(
		() =>
			buildColumnDefs(
				showCurrentCampaign ? (currentData?.header ?? null) : null,
				pageHeaders,
				showCurrentCampaign,
			),
		[currentData?.header, pageHeaders, showCurrentCampaign],
	);

	const getRowHeight = () => 28;

	if (isLoading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "calc(100vh - 100px)",
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				overflow: "hidden",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Next Campaigns
			</Typography>
			<Paper elevation={3}>
				<LegendBar page={page} totalPages={totalPages} onPageChange={setPage} />
			</Paper>

			<Paper
				elevation={3}
				sx={{
					flex: 1,
					overflowY: "auto",
					position: "relative",
					"& .ag-header-cell-label": {
						fontSize: "12px",
						fontWeight: 600,
					},
					"& .ag-right-aligned-cell": {
						textAlign: "right !important",
						justifyContent: "flex-end !important",
						paddingRight: "8px !important",
					},
					"& .ag-cell.ag-column-group-separator": {
						borderRight: "1px solid #e0e0e0 !important",
					},
				}}
			>
				<AgGridReact<GridRow>
					rowData={rowData}
					columnDefs={columnDefs}
					defaultColDef={globalDefaultColDef}
					domLayout="autoHeight"
					headerHeight={26}
					groupHeaderHeight={82}
					getRowHeight={getRowHeight}
					suppressCellFocus
					suppressContextMenu
					suppressColumnVirtualisation
					animateRows={false}
					enableBrowserTooltips
				/>
			</Paper>

			<ErrorModal
				error={
					(currentError as Error)?.message ||
					(futureError as Error)?.message ||
					error
				}
				onHide={() => setError("")}
			/>
		</Box>
	);
};
